import { z } from 'zod'
import { api } from './client'
import type { HttpResponse } from '@/types/api'
import type { Session, User } from '@/types/auth.types'

type LoginUserPayload = User & {
    userId?: string
    user_id?: string
}

// ---- Constants for Validation ----
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#&!%*?^~$+\-=_[\]{}()|;:'",.<>/\\]).{8,}$/
const PASSWORD_ERROR_MSG = 'Mật khẩu tối thiểu 8 ký tự bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt (ví dụ: @, #, & ....)'

// ---- Schemas ----
export const loginSchema = z.object({
    username: z.string().trim().min(1, 'Vui lòng nhập email hoặc tên đăng nhập'),
    password: z.string().trim().min(1, 'Vui lòng nhập mật khẩu'),
}).required()

export const forgotEmailSchema = z.object({
    email: z.email('Email không hợp lệ')
        .trim()
        .min(1, 'Vui lòng nhập email')
    ,
}).required()

export const checkOtpSchema = z.object({
    email: z.email('Email không hợp lệ')
        .trim()
        .min(1, 'Vui lòng nhập email')
    ,
    otp: z.string()
        .trim()
        .min(1, 'Vui lòng nhập mã OTP')
        .length(6, 'Vui lòng nhập đủ 6 số mã OTP'),
}).required()

export const resetPasswordWithTokenSchema = z.object({
    email: z.email('Email không hợp lệ')
        .trim()
        .min(1, 'Vui lòng nhập email')
    ,

    password: z.string().trim()
        .min(1, 'Vui lòng nhập mật khẩu mới')
        .regex(PASSWORD_REGEX, PASSWORD_ERROR_MSG),

    confirmPassword: z.string().trim()
        .min(1, 'Vui lòng xác nhận mật khẩu mới'),

    token: z.string().trim()
        .min(1, 'Token xác thực không hợp lệ'),
}).required().refine((data) => data.password === data.confirmPassword, {
    message: 'Mật khẩu xác nhận không khớp',
    path: ['confirmPassword'],
})

export const changePasswordSchema = z.object({
    email: z.email('Email không hợp lệ')
        .trim()
        .min(1, 'Vui lòng nhập email')
    ,

    currentPassword: z.string().trim()
        .min(1, 'Vui lòng nhập mật khẩu hiện tại'),

    newPassword: z.string().trim()
        .min(1, 'Vui lòng nhập mật khẩu mới')
        .regex(PASSWORD_REGEX, PASSWORD_ERROR_MSG),

    confirmPassword: z.string().trim()
        .min(1, 'Vui lòng xác nhận mật khẩu mới'),
}).required().refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Mật khẩu xác nhận không khớp',
    path: ['confirmPassword'],
})

export type LoginRequest = z.infer<typeof loginSchema>

interface LoginUserResponse {
    id: string
    username: string
    email: string
    fullName?: string | null
    phoneNumber?: string | null
    description?: string | null
    status?: number
    createdAt?: string
    unitId?: string
    unitName?: string
    groupPermissionId?: string
    groupPermissionName?: string
    userType?: number
    loginCount?: number
}

interface LoginResponse {
    access_token?: string
    accessToken?: string
    refresh_token?: string
    refreshToken?: string
    userResponse?: LoginUserResponse
    user?: LoginUserPayload
    userId?: string
    permissions?: string[]
}

interface LegacyHttpResponse<T> {
    responseCode?: string
    responseMessage?: string
    responseData?: T
}

function unwrapResponse<T>(
    payload: HttpResponse<T> | LegacyHttpResponse<T>,
): T {
    if ('result' in payload && payload.result !== undefined) {
        return payload.result
    }

    if ('responseData' in payload && payload.responseData !== undefined) {
        return payload.responseData
    }

    throw new Error('Invalid API response format')
}

// ---- Endpoints ----
export const authApi = {
    login: async (payload: LoginRequest): Promise<Session & { responseMessage?: string }> => {
        try {
            const res = await api.post<HttpResponse<LoginResponse> | LegacyHttpResponse<LoginResponse>>('/v2/auth/login', payload, {
                withCredentials: true,
            })

            const data = unwrapResponse<LoginResponse>(res.data)
            const accessToken = data.accessToken || data.access_token
            const refreshToken = data.refreshToken || data.refresh_token

            const userResponse = data.userResponse
            if (!accessToken) throw new Error('Missing access token in login response')
            if (!userResponse) throw new Error('Missing user info in login response')

            return {
                accessToken,
                refreshToken,
                user: userResponse,
                permissions: data.permissions ?? [],
                responseMessage: (res.data as any).responseMessage,
            }
        } catch (error: any) {
            const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi đăng nhập.';
            throw new Error(message);
        }
    },

    requestPasswordReset: async (payload: z.infer<typeof forgotEmailSchema>) => {
        try {
            const res = await api.post('/v1/auth/reset-password', payload)
            return res.data
        } catch (error: any) {
            const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi yêu cầu đặt lại mật khẩu.';
            throw new Error(message);
        }
    },

    checkOtp: async (payload: z.infer<typeof checkOtpSchema>) => {
        try {
            const res = await api.post('/v1/auth/check-otp', payload)
            return res.data
        } catch (error: any) {
            const message = error?.response?.data?.responseMessage || error?.message || 'Mã OTP không chính xác hoặc đã hết hạn.';
            throw new Error(message);
        }
    },

    resetPasswordWithToken: async (payload: z.infer<typeof resetPasswordWithTokenSchema>) => {
        try {
            const { confirmPassword, ...dataToSend } = payload
            const res = await api.post('/v1/auth/change-password', dataToSend)
            return res.data
        } catch (error: any) {
            const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi đặt lại mật khẩu.';
            throw new Error(message);
        }
    },

    changePassword: async (payload: z.infer<typeof changePasswordSchema>) => {
        try {
            const { confirmPassword, ...dataToSend } = payload
            const res = await api.post('/v1/auth/create-password', dataToSend)
            return res.data
        } catch (error: any) {
            const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi đổi mật khẩu.';
            throw new Error(message);
        }
    },

    logout: async (): Promise<HttpResponse<unknown>> => {
        try {
            const res = await api.post('/v2/auth/logout', null, {
                withCredentials: true,
            })
            return res.data
        } catch (error: any) {
            const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi đăng xuất.';
            throw new Error(message);
        }
    },
}